import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback, memo } from 'react';

import { TwemojiText } from '../../utils/appleEmoji';
import TwemojiInput from '../../components/Chat/TwemojiInput';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Image, Plus, Paperclip, Circle, Search, Trash2, X, ChevronUp, ChevronDown, Loader, Scissors, Copy, Clipboard, FileText, MousePointerClick, Info, Reply, Share2, CheckSquare, ArrowRight, Users, Flag } from 'lucide-react';
import EmojiPickerButton, { ReactionEmojiPicker } from '../../components/Chat/EmojiPickerButton';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import DeleteModal from '../../components/Common/DeleteModal';
import FriendInfoModal from '../../components/Chat/FriendInfoModal';
import GroupChatView from '../../components/Chat/GroupChatView';
import GroupInfoModal from '../../components/Chat/GroupInfoModal';
import GroupInfoSidebar from '../../components/Chat/GroupInfoSidebar';
import FriendInfoSidebar from '../../components/Chat/FriendInfoSidebar';
import ImageModal from '../../components/Chat/ImageModal';
import ImagePreviewModal from '../../components/Chat/ImagePreviewModal';
import FileConfirmModal from '../../components/Chat/FileConfirmModal';
import MessageInfoModal from '../../components/Chat/MessageInfoModal';
import ForwardModal from '../../components/Chat/ForwardModal';
import ContextMenu from '../../components/Common/ContextMenu';
import ReportMessageModal from '../../components/Chat/ReportMessageModal';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import axios from 'axios';
import { getApiBaseUrl, authApi } from '../../services/cloudApi';
import { useLocation } from 'react-router-dom';
import { getSocket } from '../../services/socketService';
import { formatMessageInfo } from '../../utils/formatMessageInfo';
import { formatLastSeen } from '../../utils/formatLastSeen';
import { getPlusEmailColor } from '../../utils/userDisplay';
import Username, { getDisplayName } from '../../components/Common/Username';
import SharedNoteMessage from '../../components/Chat/SharedNoteMessage';
import CollabInviteMessage from '../../components/Chat/CollabInviteMessage';
import EventCardMessage from '../../components/Chat/EventCardMessage';
import PollCardMessage from '../../components/Chat/PollCardMessage';
import ChatAttachmentPopover from '../../components/Chat/ChatAttachmentPopover';
import NoteListModal from '../../components/Chat/NoteListModal';
import PollCreateModal from '../../components/Chat/PollCreateModal';
import ContactShareModal from '../../components/Chat/ContactShareModal';
import ContactCardMessage from '../../components/Chat/ContactCardMessage';
import CreateEventModal from '../../components/Calendar/CreateEventModal';
import SelectCalendarModal from '../../components/Calendar/SelectCalendarModal';
import { chatApi } from '../../services/cloudApi';
import { useCalendar } from '../../contexts/CalendarContext';
import { useMembership } from '../../hooks/useMembership';
import { UPGRADE_MESSAGES } from '../../utils/membershipConstants';
import { fixFileName } from '../../utils/fixFileName';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import ChatImage from '../../components/Common/ChatImage';

const QUICK_REACTIONS = ['😂', '😮', '😢', '😍', '👍'];

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

// Helper function to strip HTML tags and truncate text
const cleanMessageText = (text, maxLength = 100) => {
  if (!text) return '';
  // Strip HTML tags
  const stripped = text.replace(/<[^>]*>/g, '');
  // Decode HTML entities
  const decoded = stripped
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  // Truncate if needed
  if (decoded.length > maxLength) {
    return decoded.substring(0, maxLength) + '...';
  }
  return decoded;
};

// Sadece emoji içerip içermediğini kontrol eder (arka plan kaldırmak için)
const isEmojiOnlyContent = (text) => {
  if (!text || typeof text !== 'string') return false;
  const stripped = text.trim();
  if (!stripped) return false;
  const withoutEmoji = stripped.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F9B0}-\u{1F9B3}\u200D\uFE0F\s]/gu, '');
  return withoutEmoji.length === 0;
};

// Helper to safely render message text with clickable URLs and optional search highlight
const renderMessageContent = (text, searchQuery, isOwnMessage) => {
  if (!text && text !== 0) return null;

  const safeText = String(text);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = safeText.split(urlRegex);

  // Helper: highlight search query inside plain text (non-link) segments
  const renderWithHighlight = (segment) => {
    const query = (searchQuery || '').trim();
    if (!query) {
      return segment;
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const highlightRegex = new RegExp(`(${escapedQuery})`, 'gi');

    return segment.split(highlightRegex).map((part, index) => {
      if (!part) return null;
      const isMatch = part.toLowerCase() === query.toLowerCase();
      if (!isMatch) {
        return <span key={index}>{part}</span>;
      }
      return (
        <mark
          key={index}
          className={`${isOwnMessage ? 'bg-yellow-400/30 text-white' : 'bg-yellow-300 text-gray-900'} px-0.5 rounded`}
        >
          {part}
        </mark>
      );
    });
  };

  // parts: even indexes = normal text, odd indexes = URLs (because of capturing group)
  return parts.map((part, index) => {
    if (!part) return null;

    const isUrl = index % 2 === 1;
    if (isUrl) {
      const url = part;
      return (
        <a
          key={`url-${index}-${url}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-600 dark:text-blue-400 break-words"
        >
          {url}
        </a>
      );
    }

    return <TwemojiText key={`text-${index}`} text={part} />;
  });
};

// Memoized component for friend status display to prevent unnecessary re-renders
const FriendStatusDisplay = memo(({ friend, isTyping, onFriendClick }) => {
  const { t } = useTranslation('chat');
  const statusText = useMemo(() => {
    if (isTyping) return null;
    return friend.statusText ||
           (friend.status === 'online' || friend.is_online === true
             ? t('chatPage.onlineStatus')
             : formatLastSeen(friend.last_seen));
  }, [friend.statusText, friend.status, friend.is_online, friend.last_seen, isTyping, t]);

  const isOnline = friend.status === 'online' || friend.is_online === true;

  return (
    <div className="flex-1 min-w-0 cursor-pointer" onClick={onFriendClick}>
      <h3 className="truncate">
        <Username user={friend} variant="default" />
      </h3>
      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-[var(--color-text-secondary)]">
        {isTyping ? (
          <span className="truncate italic">{t('chatPage.typingLabel')}</span>
        ) : (
          <>
            <Circle
              className={`w-1.5 h-1.5 flex-shrink-0 ${
                isOnline ? 'text-green-500' : 'text-gray-400 dark:text-[var(--color-text-secondary)]'
              }`}
              fill="currentColor"
            />
            <span className="truncate">{statusText}</span>
          </>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.friend?.status === nextProps.friend?.status &&
    prevProps.friend?.is_online === nextProps.friend?.is_online &&
    prevProps.friend?.statusText === nextProps.friend?.statusText &&
    prevProps.friend?.last_seen === nextProps.friend?.last_seen &&
    prevProps.isTyping === nextProps.isTyping
  );
});

FriendStatusDisplay.displayName = 'FriendStatusDisplay';

const ChatPage = () => {
  const { t, i18n } = useTranslation('chat');
  const dateLocale = getDateFnsLocale(i18n.language);

  // Helper function to format message time
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const isToday = date.toDateString() === now.toDateString();

      if (diffMins < 1) return t('chatPage.justNow');
      if (isToday) {
        return format(date, 'HH:mm', { locale: dateLocale });
      }
      return format(date, 'd MMM HH:mm', { locale: dateLocale });
    } catch (error) {
      return '';
    }
  };

  const {
    selectedFriend, 
    selectedFriendMessages, 
    selectedFriendId, 
    friends, 
    sendMessage, 
    deleteMessages, 
    deleteFriend, 
    loadUnreadCount,
    reloadMessages,
    removeDmMessage,
    updateDmMessage,
    selectFriend,
    replyToMessage, 
    setReplyToMessage, 
    forwardMessage, 
    setForwardMessage,
    loadPendingFriendRequestCount,
    loadingMessagesForFriendId,
    // Group chat
    selectedGroup,
    selectedGroupMessages,
    selectedGroupId,
    selectGroup,
    sendGroupMessage,
    groupTyping,
    groupMessageReads,
  } = useChat();
  const { isAuthenticated, user, authToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [messageText, setMessageText] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [textAreaContextMenu, setTextAreaContextMenu] = useState({ isOpen: false, x: 0, y: 0, hasSelection: false });
  const [messageContextMenu, setMessageContextMenu] = useState({ isOpen: false, x: 0, y: 0, message: null });
  const [reportModal, setReportModal] = useState({ isOpen: false, messageId: null });
  const [messageReactions, setMessageReactions] = useState({}); // { messageId: { emoji: count } }
  const reactionsSeededFriendRef = useRef(null); // prevents overwriting socket-updated reactions
  const [reactionPickerState, setReactionPickerState] = useState({ isOpen: false, x: 0, y: 0, messageId: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFriendInfoModal, setShowFriendInfoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [selectedImageFileName, setSelectedImageFileName] = useState(null);
  const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [showFileConfirmModal, setShowFileConfirmModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchMatchIndex, setSearchMatchIndex] = useState(-1);
  const [showMessageInfoModal, setShowMessageInfoModal] = useState(false);
  const [selectedMessageForInfo, setSelectedMessageForInfo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showAttachmentPopover, setShowAttachmentPopover] = useState(false);
  const [showNoteListModal, setShowNoteListModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showSelectCalendarModal, setShowSelectCalendarModal] = useState(false);
  const [selectedCalendarForEvent, setSelectedCalendarForEvent] = useState(null);
  const [showPollCreateModal, setShowPollCreateModal] = useState(false);
  const [showContactShareModal, setShowContactShareModal] = useState(false);
  const [deleteFlow, setDeleteFlow] = useState({ chooseOpen: false, confirmOpen: false, message: null, messages: null, deleteType: null });
  const [selectionForwardMessages, setSelectionForwardMessages] = useState(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());
  const attachmentTriggerRef = useRef(null);
  const { createEvent, calendars, selectedCalendar } = useCalendar();
  const [isTyping, setIsTyping] = useState(false);
  const [showGroupInfoModal, setShowGroupInfoModal] = useState(false);
  const [showGroupInfoSidebar, setShowGroupInfoSidebar] = useState(false);
  const [showFriendInfoSidebar, setShowFriendInfoSidebar] = useState(false);
  
  // Listen for media/starred messages events from GroupInfoSidebar
  useEffect(() => {
    const handleShowMedia = (event) => {
      if (selectedGroupId === event.detail.groupId) {
        setShowGroupInfoModal(true);
        // Set activeTab to 'media' in GroupInfoModal
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('notesplus-set-group-modal-tab', { 
            detail: { tab: 'media' } 
          }));
        }, 100);
      }
    };
    
    const handleShowStarred = (event) => {
      if (selectedGroupId === event.detail.groupId) {
        setShowGroupInfoModal(true);
        // Set activeTab to 'starred' in GroupInfoModal
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('notesplus-set-group-modal-tab', { 
            detail: { tab: 'starred' } 
          }));
        }, 100);
      }
    };
    
    window.addEventListener('notesplus-show-group-media', handleShowMedia);
    window.addEventListener('notesplus-show-starred-messages', handleShowStarred);
    
    return () => {
      window.removeEventListener('notesplus-show-group-media', handleShowMedia);
      window.removeEventListener('notesplus-show-starred-messages', handleShowStarred);
    };
  }, [selectedGroupId]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);
  const messageRefs = useRef({});
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const prevSelectedFriendIdForAnimRef = useRef(null);

  const typingTimeoutRef = useRef(null);
  const isTypingActiveRef = useRef(false);
  const hasMarkedAsReadOnFocusRef = useRef(false); // Prevent multiple read marks on focus

  // Filter messages by search query and get match indices
  // Results are sorted in reverse order: newest message is 1, oldest is last
  const searchMatches = useMemo(() => {
    if (!messageSearchQuery.trim() || !selectedFriendMessages) return [];
    const query = messageSearchQuery.toLowerCase();
    const matches = selectedFriendMessages
      .map((msg, index) => ({
        message: msg,
        index,
      }))
      .filter(({ message }) => message.content.toLowerCase().includes(query));
    
    // Reverse the order so newest messages come first (1/5, 2/5, etc.)
    return matches.reverse();
  }, [selectedFriendMessages, messageSearchQuery]);

  // Track if user has scrolled up manually
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // column-reverse kullanıldığında en altta (en son mesaj) scrollTop ≈ 0
  useEffect(() => {
    if (!messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    const handleScroll = () => {
      const isAtBottom = container.scrollTop < 50;
      setIsUserScrolledUp(!isAtBottom);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset scroll state and reply when friend changes
  useEffect(() => {
    setIsUserScrolledUp(false);
    setReplyToMessage(null);
    setIsSelectionMode(false);
    setSelectedMessageIds(new Set());
  }, [selectedFriendId, setReplyToMessage]);

  // Scroll sabitleme: sessiz sync sonrası zıplamayı önlemek için önceki scrollHeight/scrollTop saklanır, DOM güncellenince fark kadar tazminat
  const scrollAnchorRef = useRef({ scrollHeight: 0, scrollTop: 0, messageCount: 0, friendId: null });
  const prevSelectedFriendIdRefForScroll = useRef(null);

  // İlk açılışta anında scroll (animasyonsuz); sessiz sync sonrası tazminat. Sonunda prevSelectedFriendIdForAnimRef güncellenir (ilk açılışta animasyon kapatmak için).
  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (!selectedFriendId || !el) return;
    const messageCount = selectedFriendMessages?.length ?? 0;
    const friendChanged = prevSelectedFriendIdRefForScroll.current !== selectedFriendId;
    prevSelectedFriendIdRefForScroll.current = selectedFriendId;

    const prev = scrollAnchorRef.current;
    const sameFriend = prev.friendId === selectedFriendId;
    const countIncreased = messageCount > prev.messageCount;

    if (friendChanged) {
      el.scrollTop = 0;
      scrollAnchorRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop, messageCount, friendId: selectedFriendId };
      prevSelectedFriendIdForAnimRef.current = selectedFriendId;
      return;
    }

    if (sameFriend && countIncreased && prev.scrollHeight > 0) {
      const deltaH = el.scrollHeight - prev.scrollHeight;
      if (deltaH > 0) {
        el.scrollTop = prev.scrollTop + deltaH;
      }
    } else if (sameFriend && messageCount > 0 && prev.messageCount === 0) {
      el.scrollTop = 0;
    }

    scrollAnchorRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop, messageCount, friendId: selectedFriendId };
    prevSelectedFriendIdForAnimRef.current = selectedFriendId;
  }, [selectedFriendId, selectedFriendMessages?.length]);

  // ── Mesaj animasyon sistemi ──────────────────────────────────────────────
  // Kural: Sadece bu render'da LİSTEYE YENİ EKLENMİŞ mesajlar animasyon alır.
  // Yani önceki render'da zaten vardı ise → animasyon yok.
  // Temp → gerçek ID geçişi = aynı mesaj, tekrar animasyon OLMAMALI.

  // filteredMessages burada tanımlanmalı — aşağıdaki animation kodu buna ihtiyaç duyuyor
  const filteredMessages = selectedFriendMessages;

  const prevSnapshotRef = useRef({ friendId: null, ids: new Set(), tempCount: 0 });
  const noAnimIdsRef = useRef(new Set());   // Bu render'da animasyon almayacak ID'ler

  // Render SIRASINDA (senkron) hesapla — useEffect değil, çünkü sonra çok geç
  const currentMsgs = filteredMessages || [];
  const snap = prevSnapshotRef.current;
  const friendChanged = snap.friendId !== selectedFriendId;

  // Temp → gerçek geçişi tespiti: önceki snapshat'ta temp varsa ve şimdi azaldıysa
  if (!friendChanged && snap.tempCount > 0) {
    const currTempCount = currentMsgs.filter(m => String(m.id).startsWith('temp_')).length;
    if (currTempCount < snap.tempCount) {
      // Server yanıtı geldi, yeni gerçek ID'ler aslında temp'in yerini aldı → animasyon yok
      currentMsgs.forEach(m => {
        const sid = String(m.id);
        if (!snap.ids.has(sid)) noAnimIdsRef.current.add(sid);
      });
    }
  }

  // Snapshot'ı bu render sonrası güncelle (layout bitmeden önce — paint'ten önce)
  useLayoutEffect(() => {
    const msgs = filteredMessages || [];
    prevSnapshotRef.current = {
      friendId: selectedFriendId,
      ids: new Set(msgs.map(m => String(m.id))),
      tempCount: msgs.filter(m => String(m.id).startsWith('temp_')).length,
    };
    // Artık settled olan noAnimIds'leri temizle
    noAnimIdsRef.current.clear();
  });

  // İlk açılış — tüm geçmiş mesajlar animasyonsuz gelsin
  const isFirstOpenForChat = friendChanged;

  // Kullanıcı zaten sohbetteyken yeni mesaj geldiğinde veya kendisi gönderdiğinde en alta kaydır (anında; column-reverse'te scrollTop=0)
  useEffect(() => {
    const currentLength = selectedFriendMessages?.length || 0;
    const prevLength = prevMessagesLengthRef.current;
    if (currentLength > prevLength && !isUserScrolledUp && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
    prevMessagesLengthRef.current = currentLength;
  }, [selectedFriendMessages?.length, isUserScrolledUp]);

  // Track if chat was just opened (first time selecting a friend)
  const chatJustOpenedRef = useRef(false);
  const prevSelectedFriendIdRef = useRef(null);

  // Focus input ONLY when chat is first opened (friend selected for the first time)
  useEffect(() => {
    if (isAuthenticated && user && selectedFriendId && inputRef.current) {
      // Check if this is a new friend selection (chat just opened)
      const isNewChat = prevSelectedFriendIdRef.current !== selectedFriendId;
      
      if (isNewChat) {
        // Chat just opened - focus input
        chatJustOpenedRef.current = true;
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
          chatJustOpenedRef.current = false;
        }, 100);
      }
      
      prevSelectedFriendIdRef.current = selectedFriendId;
    }
  }, [selectedFriendId, isAuthenticated, user]);

  // Scroll to search match
  useEffect(() => {
    if (isAuthenticated && user && searchMatchIndex >= 0 && searchMatchIndex < searchMatches.length && searchMatches[searchMatchIndex]) {
      const messageId = searchMatches[searchMatchIndex].message.id;
      const messageElement = messageRefs.current[messageId];
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [searchMatchIndex, searchMatches, isAuthenticated, user]);

  // Reset search match index when search query changes
  useEffect(() => {
    if (isAuthenticated && user) {
      if (messageSearchQuery.trim()) {
        setSearchMatchIndex(0);
      } else {
        setSearchMatchIndex(-1);
      }
    }
  }, [messageSearchQuery, isAuthenticated, user]);

  // "Mesaja Git" — from FriendInfoModal media tab
  useEffect(() => {
    const handleScrollToDm = (e) => {
      const { friendId, messageId } = e.detail || {};
      if (!friendId || !messageId) return;

      // Select the friend first
      if (selectFriend) selectFriend(friendId);

      // Then scroll to the message once messages are rendered
      const attemptScroll = (tries = 0) => {
        const el = messageRefs.current[messageId];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight briefly
          el.style.transition = 'background 0.3s';
          el.style.background = 'rgba(61,142,240,0.15)';
          setTimeout(() => { el.style.background = ''; }, 1500);
        } else if (tries < 20) {
          setTimeout(() => attemptScroll(tries + 1), 150);
        }
      };
      setTimeout(() => attemptScroll(), 300);
    };

    window.addEventListener('notesplus-scroll-to-message-dm', handleScrollToDm);
    return () => window.removeEventListener('notesplus-scroll-to-message-dm', handleScrollToDm);
  }, [selectFriend]);

  // Debounce timer for markMessagesAsRead
  const markAsReadDebounceTimerRef = useRef(null);
  const lastMarkedAsReadRef = useRef(new Set()); // Track recently marked messages to prevent duplicates

  // Mark messages as read when input is focused or typing starts (debounced)
  const markMessagesAsRead = useCallback(() => {
    if (!selectedFriendId || !isAuthenticated || !authToken) return;

    // Clear existing debounce timer
    if (markAsReadDebounceTimerRef.current) {
      clearTimeout(markAsReadDebounceTimerRef.current);
    }

    // Debounce: wait 500ms before marking as read
    markAsReadDebounceTimerRef.current = setTimeout(() => {
      const socket = getSocket();
      if (!socket || !socket.connected) return;

      // Mark all unread messages from this friend as read
      const friendMessages = selectedFriendMessages || [];
      const messagesToMark = [];
      
      friendMessages.forEach((msg) => {
        if (msg.senderId !== 'current_user' && 
            !msg.isRead && 
            msg.id && 
            !msg.id.startsWith('temp_') &&
            !lastMarkedAsReadRef.current.has(msg.id)) {
          messagesToMark.push(msg.id);
          lastMarkedAsReadRef.current.add(msg.id);
        }
      });

      // Emit read events for messages that need to be marked
      messagesToMark.forEach((messageId) => {
        socket.emit('message:read', { message_id: messageId });
      });

      // Optimistically update local state - mark messages as read immediately
      if (messagesToMark.length > 0 && selectedFriendId) {
        // Update messages state to mark as read
        // This will be confirmed by the server response, but we update optimistically
        // Note: We need to access messages from ChatContext, but we can't directly modify it here
        // The server will send message:read event which will update the state
        // But we also need to update local state here to remove unread indicators immediately
      }

      // Clean up old entries from lastMarkedAsReadRef (keep only last 100)
      if (lastMarkedAsReadRef.current.size > 100) {
        const entries = Array.from(lastMarkedAsReadRef.current);
        lastMarkedAsReadRef.current = new Set(entries.slice(-50));
      }

      // Reload unread count to ensure consistency
      if (messagesToMark.length > 0) {
        setTimeout(() => {
          loadUnreadCount();
        }, 500);
      }
    }, 500); // 500ms debounce
  }, [selectedFriendId, selectedFriendMessages, isAuthenticated, authToken, loadUnreadCount]);

  // Helper function to check if chat is active (all conditions must be met)
  const isChatActive = useCallback(() => {
    const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat');
    const isVisible = document.visibilityState === 'visible';
    const isFocused = document.hasFocus();
    const socket = getSocket();
    const isSocketConnected = socket && socket.connected;
    
    return isOnChatPage && isVisible && isFocused && isSocketConnected;
  }, [location.pathname]);

  // Handle search close
  const handleSearchClose = useCallback(() => {
    setShowSearchBar(false);
    setMessageSearchQuery('');
    setSearchMatchIndex(-1);
  }, []);

  // Handle ESC key to close chat
  const handleCloseChat = useCallback(() => {
    if (selectFriend) {
      selectFriend(null);
    }
  }, [selectFriend]);

  const { limits } = useMembership();
  const maxUploadBytes = limits?.maxUploadBytes ?? 15 * 1024 * 1024;

  const showUploadToast = (type, title, message) => {
    window.dispatchEvent(
      new CustomEvent('notesplus-show-toast', {
        detail: { type, title, message },
      })
    );
  };

  const uploadWithType = useCallback(async (uploadType, file, options = {}) => {
    if (!selectedFriendId || !authToken || !file) return null;
    if (file.size > maxUploadBytes) {
      showUploadToast('error', t('chatPage.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
      return null;
    }
    const { caption, onSuccess } = options;
    setUploadingFile(true);
    setUploadProgress(0);
    try {
      const apiBaseUrl = await getApiBaseUrl();
      const url = `${apiBaseUrl}/chat/upload?type=${uploadType}`;
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        },
      });
      if (!data?.ok || !data?.fileUrl) {
        showUploadToast('error', t('common:errorTitle'), data?.message || t('chatPage.uploadFailedFallback'));
        return null;
      }
      const relativeFileUrl = data.fileUrl.startsWith('/') ? data.fileUrl : `/${data.fileUrl}`;
      if (uploadType === 'image') {
        const messageContent = (caption && caption.trim()) || data.fileName || file.name;
        await sendMessage(selectedFriendId, messageContent, 'image', relativeFileUrl, data.fileName);
      } else {
        await sendMessage(selectedFriendId, data.fileName || file.name, 'file', relativeFileUrl, data.fileName);
      }
      onSuccess?.();
      setTimeout(() => inputRef.current?.focus(), 100);
      return data;
    } catch (err) {
      const serverMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        t('chatPage.fileUploadErrorFallback');
      const title = err.response?.status === 413 ? t('chatPage.uploadSizeLimitTitle') : t('chatPage.sendErrorTitle');
      showUploadToast('error', title, serverMessage);
      return null;
    } finally {
      setUploadProgress(null);
      setUploadingFile(false);
    }
  }, [selectedFriendId, authToken, sendMessage, maxUploadBytes]);

  // Update status when entering/leaving chat section (Socket.IO realtime)
  // Note: Socket listeners are handled in ChatContext, we only emit events here
  // CRITICAL: Only depend on location.pathname and selectedFriendId to prevent unnecessary re-runs
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;

    const socket = getSocket();
    if (!socket || !socket.connected) return;

    // Check if chat is active (all conditions met)
    // Call isChatActive directly (it's stable via useCallback)
    const chatActive = isChatActive();
    if (chatActive) {
      socket.emit('user:online');
      // Mark messages as read when chat page is opened
      if (selectedFriendId) {
        // Call markMessagesAsRead directly (it's stable via useCallback)
        markMessagesAsRead();
      }
    } else {
      // Not active - emit offline
      socket.emit('user:offline');
    }

    // Cleanup: Update status when leaving chat
    return () => {
      const cleanupSocket = getSocket();
      if (cleanupSocket && cleanupSocket.connected) {
        cleanupSocket.emit('user:offline');
      }
      // Clear debounce timer on cleanup
      if (markAsReadDebounceTimerRef.current) {
        clearTimeout(markAsReadDebounceTimerRef.current);
        markAsReadDebounceTimerRef.current = null;
      }
    };
    // CRITICAL: Only depend on location.pathname and selectedFriendId
    // isChatActive and markMessagesAsRead are stable via useCallback, no need to include them
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectedFriendId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle visibility change (ALT-TAB, minimize, switch tab, etc.)
  // Note: Socket listeners are handled in ChatContext, we only emit events here
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;

    const socket = getSocket();
    if (!socket || !socket.connected) return;

    const handleVisibilityChange = () => {
      if (isChatActive()) {
        // All conditions met - user is online
        socket.emit('user:online');
      } else {
        // Any condition failed - user is offline
        socket.emit('user:offline');
        // Stop typing when going offline
        if (isTypingActiveRef.current && selectedFriendId) {
          socket.emit('typing:stop', { receiver_id: selectedFriendId });
          isTypingActiveRef.current = false;
        }
      }
    };

    const handleFocus = () => {
      if (isChatActive()) {
        socket.emit('user:online');
      } else {
        socket.emit('user:offline');
      }
    };

    const handleBlur = () => {
      socket.emit('user:offline');
      // Stop typing when window loses focus
      if (isTypingActiveRef.current && selectedFriendId) {
        socket.emit('typing:stop', { receiver_id: selectedFriendId });
        isTypingActiveRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [location.pathname, isAuthenticated, authToken, selectedFriendId, isChatActive]);

  // Handle typing indicator from custom event (dispatched by ChatContext)
  // Note: Socket listeners are handled in ChatContext, we only listen to custom events here
  useEffect(() => {
    if (!isAuthenticated || !authToken || !selectedFriendId) {
      setIsTyping(false);
      return;
    }

    const handleTypingEvent = (event) => {
      const { from, isTyping: typingStatus } = event.detail;
      const friendId = selectedFriend?.email || selectedFriendId;
      
      // Only show typing if it's from the current selected friend
      if (from === friendId) {
        setIsTyping(typingStatus);
      } else {
        setIsTyping(false);
      }
    };

    window.addEventListener('notesplus-typing', handleTypingEvent);

    return () => {
      window.removeEventListener('notesplus-typing', handleTypingEvent);
      setIsTyping(false);
    };
  }, [isAuthenticated, authToken, selectedFriendId, selectedFriend]);

  // Handle window focus/blur (ALT-TAB, minimize, etc.)
  // Note: Socket listeners are handled in ChatContext, we only emit events here
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;

    const socket = getSocket();
    if (!socket || !socket.connected) return;

    const handleWindowBlur = () => {
      // Window lost focus (ALT-TAB, minimize, etc.)
      if (socket && socket.connected) {
        socket.emit('user:offline');
        // Stop typing when window loses focus
        if (isTypingActiveRef.current && selectedFriendId) {
          socket.emit('typing:stop', { receiver_id: selectedFriendId });
          isTypingActiveRef.current = false;
        }
      }
    };

    const handleWindowFocus = () => {
      const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat');
      
      // Window gained focus and chat is open
      if (isOnChatPage && socket && socket.connected) {
        socket.emit('user:online');
        // Mark messages as read when window gains focus and chat is open
        if (selectedFriendId) {
          markMessagesAsRead();
        }
      }
    };

    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      // Clear debounce timer on cleanup
      if (markAsReadDebounceTimerRef.current) {
        clearTimeout(markAsReadDebounceTimerRef.current);
        markAsReadDebounceTimerRef.current = null;
      }
    };
  }, [location.pathname, isAuthenticated, authToken, selectedFriendId, markMessagesAsRead]);

  // Mark messages as read when friend is selected (chat opened)
  useEffect(() => {
    if (selectedFriendId && isAuthenticated && authToken) {
      // Mark messages as read when friend is selected
      markMessagesAsRead();
    }
  }, [selectedFriendId, isAuthenticated, authToken, markMessagesAsRead]);

  // Cleanup typing when friend changes or component unmounts
  useEffect(() => {
    // Reset focus flag when friend changes
    hasMarkedAsReadOnFocusRef.current = false;
    // Clear last marked messages when friend changes
    lastMarkedAsReadRef.current.clear();
    
    return () => {
      // Clear typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      // Clear mark as read debounce timer
      if (markAsReadDebounceTimerRef.current) {
        clearTimeout(markAsReadDebounceTimerRef.current);
        markAsReadDebounceTimerRef.current = null;
      }

      // Stop typing if active
      if (isTypingActiveRef.current && selectedFriendId) {
        const socket = getSocket();
        if (socket && socket.connected) {
          socket.emit('typing:stop', { receiver_id: selectedFriendId });
        }
        isTypingActiveRef.current = false;
      }
    };
  }, [selectedFriendId]);

  // Focus search input when search bar opens
  useEffect(() => {
    if (isAuthenticated && user && showSearchBar && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearchBar, isAuthenticated, user]);

  // ESC key handler - Close chat if no modals are open
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if any modal is open
      const isModalOpen = showDeleteModal || showFriendInfoModal || showImageModal || showImagePreviewModal || showFileConfirmModal || showMessageInfoModal;
      
      // If a modal is open, let the modal handle ESC (they have their own handlers)
      if (isModalOpen) {
        return;
      }

      // If search bar is open, close it first
      if (showSearchBar && event.key === 'Escape') {
        handleSearchClose();
        return;
      }

      // If chat is open and no modals/search, close chat
      if (event.key === 'Escape' && selectedFriendId) {
        handleCloseChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFriendId, showDeleteModal, showFriendInfoModal, showImageModal, showImagePreviewModal, showFileConfirmModal, showMessageInfoModal, showSearchBar, handleSearchClose, handleCloseChat]);

  // Arkadaş değişince reactions state'ini sıfırla
  useEffect(() => {
    setMessageReactions({});
    reactionsSeededFriendRef.current = null;
  }, [selectedFriendId]);

  // Mesajlar yüklenince reactions state'ini DB'den bir kez tohumla (socket/API güncellemelerini ezmez)
  useEffect(() => {
    if (!selectedFriendMessages?.length) return;
    if (reactionsSeededFriendRef.current === selectedFriendId) return; // already seeded for this friend
    reactionsSeededFriendRef.current = selectedFriendId;

    const initial = {};
    selectedFriendMessages.forEach(msg => {
      if (msg.reactions && Object.keys(msg.reactions).length > 0) {
        const counts = {};
        Object.entries(msg.reactions).forEach(([emoji, users]) => {
          if (Array.isArray(users) && users.length > 0) counts[emoji] = users.length;
        });
        if (Object.keys(counts).length > 0) initial[msg.id] = counts;
      }
    });
    if (Object.keys(initial).length > 0) {
      setMessageReactions(prev => ({ ...prev, ...initial }));
    }
  }, [selectedFriendMessages, selectedFriendId]);

  // Socket: karşı tarafın reaksiyon güncellemelerini dinle
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const handler = ({ messageId, reactions }) => {
      const counts = {};
      Object.entries(reactions || {}).forEach(([emoji, users]) => {
        if (Array.isArray(users) && users.length > 0) counts[emoji] = users.length;
      });
      setMessageReactions(prev => ({ ...prev, [messageId]: counts }));
    };
    socket.on('message:reaction', handler);
    return () => socket.off('message:reaction', handler);
  }, []);

  // Authentication check - Show login prompt if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="h-full flex items-center justify-center"
      >
        <div className="text-center text-gray-600 dark:text-[var(--color-text-secondary)] max-w-md px-6">
          <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center mb-6 mx-auto border border-slate-200 dark:border-white/10">
            <MessageCircle className="w-10 h-10 text-slate-900 dark:text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800 dark:text-white">
            {t('chatPage.loginRequiredTitle')}
          </h2>
          <p className="text-sm text-gray-600 dark:dark:text-[var(--color-text-secondary)] mb-6">
            {t('chatPage.loginRequiredMessage')}
          </p>
          <div className="flex items-center justify-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-slate-100 dark:bg-[var(--color-bg-elevated)] hover:bg-slate-200 dark:hover:bg-[#24354f] text-gray-900 dark:text-white rounded-xl font-semibold transition-all duration-200 border border-slate-200 dark:border-white/10"
            >
              {t('chatPage.loginButton')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/register')}
              className="px-6 py-3 bg-white dark:bg-[var(--color-bg-elevated)] hover:bg-gray-50 dark:hover:bg-[#24354f] text-gray-700 dark:text-[var(--color-text-secondary)] rounded-xl font-semibold transition-all duration-200 border border-slate-200 dark:border-white/10"
            >
              {t('chatPage.registerButton')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  const handleSendMessage = (e) => {
    e?.preventDefault();
    const currentText = (inputRef.current?.getText?.() ?? messageText).trim();
    if (!currentText || !selectedFriendId) return;

    // Stop typing when sending message
    const socket = getSocket();
    if (socket && socket.connected && isTypingActiveRef.current) {
      socket.emit('typing:stop', { receiver_id: selectedFriendId });
      isTypingActiveRef.current = false;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Send message with replyTo if exists
    const replyToId = replyToMessage?.id || null;
    sendMessage(selectedFriendId, currentText, 'text', null, null, replyToId);
    setMessageText('');
    inputRef.current?.clear?.();
    setIsComposing(false);
    // Reply will be cleared in ChatContext after sending

    // Focus input after sending message
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleEmojiSelect = (emoji) => {
    // Check if friend still exists in friends list and is not deleted
    const friend = selectedFriendId && friends.find(f => {
      const friendId = f.email || f.id;
      return friendId === selectedFriendId && !f.isDeleted;
    });
    if (!friend) return;

    inputRef.current?.insertEmoji?.(emoji);
  };

  // Context menu handlers for contentEditable input
  const handleCut = () => {
    inputRef.current?.focus?.();
    const sel = window.getSelection();
    if (sel && sel.toString()) {
      navigator.clipboard?.writeText?.(sel.toString()).catch(() => {});
      document.execCommand('delete');
      const remaining = inputRef.current?.getText?.() || '';
      setMessageText(remaining);
      setIsComposing(remaining.trim().length > 0);
    }
    setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
  };

  const handleCopy = () => {
    const sel = window.getSelection();
    if (sel && sel.toString()) {
      navigator.clipboard?.writeText?.(sel.toString()).catch(() => {});
    }
    setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
  };

  const handlePaste = async () => {
    inputRef.current?.focus?.();
    try {
      const text = await navigator.clipboard.readText();
      document.execCommand('insertText', false, text);
      const newText = inputRef.current?.getText?.() || '';
      setMessageText(newText);
      setIsComposing(newText.trim().length > 0);
    } catch (error) {
      console.error('Paste failed:', error);
    }
    setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
  };

  const handlePasteAsPlainText = async () => {
    inputRef.current?.focus?.();
    try {
        // Get clipboard content as plain text
        const clipboardItems = await navigator.clipboard.read();
        let plainText = '';
        
        for (const item of clipboardItems) {
          if (item.types.includes('text/plain')) {
            const blob = await item.getType('text/plain');
            plainText = await blob.text();
            break;
          }
        }
        
        // If no plain text found, try readText as fallback
        if (!plainText) {
          plainText = await navigator.clipboard.readText();
        }
        
        // Remove HTML tags if any
        plainText = plainText.replace(/<[^>]*>/g, '');
        
        document.execCommand('insertText', false, plainText);
        const newText = inputRef.current?.getText?.() || '';
        setMessageText(newText);
        setIsComposing(newText.trim().length > 0);
      } catch (error) {
        console.error('Paste as plain text failed:', error);
        handlePaste();
      }
      setTextAreaContextMenu({ isOpen: false, x: 0, y: 0 });
  };

  const handleSelectAll = () => {
    inputRef.current?.focus?.();
    document.execCommand('selectAll');
    setTextAreaContextMenu({ isOpen: false, x: 0, y: 0, hasSelection: false });
  };

  // Emoji reaction handlers
  const handleAddReaction = async (emoji, msgId) => {
    const id = msgId || messageContextMenu.message?.id;
    if (!id) return;
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
    setReactionPickerState({ isOpen: false, x: 0, y: 0, messageId: null });
    try {
      const result = await chatApi.reactToMessage({ token: authToken, messageId: id, emoji });
      if (result?.ok && result.reactions) {
        const counts = {};
        Object.entries(result.reactions).forEach(([e, users]) => {
          if (Array.isArray(users) && users.length > 0) counts[e] = users.length;
        });
        setMessageReactions(prev => ({ ...prev, [id]: counts }));
      }
    } catch (_) {
      // Hata olsa bile optimistik UI kalır
    }
  };

  // Message context menu handlers
  const handleCopyMessage = () => {
    if (messageContextMenu.message) {
      const textToCopy = messageContextMenu.message.type === 'text'
        ? messageContextMenu.message.content
        : messageContextMenu.message.file_name || messageContextMenu.message.content || t('groupChatView.messageDefaultLabel');
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(textToCopy).catch(() => {});
      } else {
        try {
          const ta = document.createElement('textarea');
          ta.value = textToCopy;
          ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (_) {}
      }
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('chatPage.copiedTitle'), message: t('chatPage.copiedMessageSingle') },
      }));
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleShowMessageInfo = () => {
    if (messageContextMenu.message) {
      setSelectedMessageForInfo(messageContextMenu.message);
      setShowMessageInfoModal(true);
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      const res = await fetch(fileUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fixFileName(fileName || t('chatPage.fileDefaultName').toLowerCase());
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const replyToMsg = (message) => {
    if (!message || message.is_deleted_for_everyone) return;
    let senderName = t('chatPage.unknownFallback');
    if (message.senderId === 'current_user') {
      senderName = user?.name || (user?.email ? user.email.split('@')[0] : t('chatPage.youShort'));
    } else if (selectedFriend) {
      senderName = selectedFriend.nickname || selectedFriend.name || (selectedFriend.email ? selectedFriend.email.split('@')[0] : t('chatPage.unknownFallback'));
    }
    const messageText = message.type === 'text'
      ? cleanMessageText(message.content, 150)
      : (message.file_name || message.content || t('chatPage.fileDefaultName'));
    setReplyToMessage({ id: message.id, text: messageText, senderName });
  };

  const handleReplyMessage = () => {
    if (messageContextMenu.message) {
      replyToMsg(messageContextMenu.message);
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleForwardMessage = () => {
    if (messageContextMenu.message) {
      const message = messageContextMenu.message;

      // Block collab_invite forwarding
      if (message.type === 'collab_invite') {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: { type: 'warning', title: t('chatPage.collabInviteWarningTitle'), message: t('chatPage.collabInviteWarningMessage') },
        }));
        setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
        return;
      }

      // Get sender name for the forward preview
      let senderName = t('chatPage.unknownFallback');
      if (message.senderId === 'current_user') {
        senderName = user?.name || (user?.email ? user.email.split('@')[0] : t('chatPage.youShort'));
      } else if (selectedFriend) {
        senderName = selectedFriend.nickname || selectedFriend.name || (selectedFriend.email ? selectedFriend.email.split('@')[0] : t('chatPage.unknownFallback'));
      }

      // Get message text
      const messageText = message.type === 'text'
        ? cleanMessageText(message.content, 200)
        : (message.file_name || message.content || t('chatPage.fileDefaultName'));

      setForwardMessage({
        id: message.id,
        text: messageText,
        senderName: senderName,
        type: message.type || 'text',
        content: message.content,
        file_url: message.file_url,
        fileUrl: message.file_url,
        file_name: message.file_name,
        note_id: message.note_id || null,
        note_title: message.note_title || null,
        note_content: message.note_content || null,
        note_title_color: message.note_title_color || null,
        event_id: message.event_id || null,
        event_title: message.event_title || null,
        event_start: message.event_start || null,
        event_end: message.event_end || null,
        event_all_day: message.event_all_day || null,
        event_description: message.event_description || null,
        event_location: message.event_location || null,
        poll_id: message.poll_id || null,
        poll_title: message.poll_title || null,
        contact_user_id: message.contact_user_id || null,
        contact_name: message.contact_name || null,
        contact_avatar_url: message.contact_avatar_url || null,
        contact_email: message.contact_email || null,
      });

      setShowForwardModal(true);
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleSelectMessage = () => {
    if (messageContextMenu.message) {
      setIsSelectionMode(true);
      setSelectedMessageIds(new Set([messageContextMenu.message.id]));
    }
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const toggleMessageSelection = (messageId) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedMessageIds(new Set());
    setSelectionForwardMessages(null);
  };

  const handleSelectionForward = () => {
    if (selectedMessageIds.size === 0) return;
    const selectedMsgs = filteredMessages.filter((m) => selectedMessageIds.has(m.id) && !m.is_deleted_for_everyone);
    if (selectedMsgs.length === 0) return;

    // Filter out collab_invite messages and warn
    const collabMsgs = selectedMsgs.filter((m) => m.type === 'collab_invite');
    const forwardableMsgs = selectedMsgs.filter((m) => m.type !== 'collab_invite');

    if (collabMsgs.length > 0) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'warning', title: t('chatPage.collabInviteWarningTitle'), message: t('chatPage.collabInviteWarningMessage') },
      }));
    }
    if (forwardableMsgs.length === 0) return;

    // Build forward-ready message objects with all type-specific fields
    const msgs = forwardableMsgs.map((m) => ({
      id: m.id,
      type: m.type || 'text',
      content: m.content,
      text: m.content,
      file_url: m.file_url,
      fileUrl: m.file_url,
      file_name: m.file_name,
      note_id: m.note_id || null,
      note_title: m.note_title || null,
      note_content: m.note_content || null,
      note_title_color: m.note_title_color || null,
      event_id: m.event_id || null,
      event_title: m.event_title || null,
      event_start: m.event_start || null,
      event_end: m.event_end || null,
      event_all_day: m.event_all_day || null,
      event_description: m.event_description || null,
      event_location: m.event_location || null,
      poll_id: m.poll_id || null,
      poll_title: m.poll_title || null,
      contact_user_id: m.contact_user_id || null,
      contact_name: m.contact_name || null,
      contact_avatar_url: m.contact_avatar_url || null,
      contact_email: m.contact_email || null,
    }));
    setForwardMessage(msgs.length === 1 ? msgs[0] : null);
    setSelectionForwardMessages(msgs);
    setShowForwardModal(true);
  };

  const handleSelectionDelete = () => {
    if (selectedMessageIds.size === 0) return;
    const selectedMsgs = filteredMessages.filter((m) => selectedMessageIds.has(m.id) && !m.is_deleted_for_everyone);
    if (selectedMsgs.length === 0) return;
    const allOwn = selectedMsgs.every((m) => m.senderId === 'current_user');
    if (allOwn) {
      // Show choose modal (Herkesten Sil / Benden Sil)
      setDeleteFlow({ chooseOpen: true, confirmOpen: false, message: null, messages: selectedMsgs, deleteType: null });
    } else {
      // Has others' messages — go directly to confirm for "Benden Sil"
      setDeleteFlow({ chooseOpen: false, confirmOpen: true, message: null, messages: selectedMsgs, deleteType: 'for_me' });
    }
  };

  const copySelectedMessages = () => {
    const selectedMsgs = filteredMessages.filter((m) => selectedMessageIds.has(m.id));
    const text = selectedMsgs
      .map((m) => m.content || m.file_name || '')
      .filter(Boolean)
      .join('\n');
    if (text) {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      } else {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (_) {}
      }
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('chatPage.copiedTitle'), message: t('chatPage.copiedMessagesPlural', { count: selectedMsgs.length }) },
      }));
    }
    exitSelectionMode();
  };

  const openDeleteFlow = () => {
    const msg = messageContextMenu.message;
    setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
    if (!msg) return;
    setDeleteFlow({ chooseOpen: true, confirmOpen: false, message: msg, deleteType: null });
  };

  const chooseDeleteType = (type) => {
    setDeleteFlow((prev) => ({ ...prev, chooseOpen: false, confirmOpen: true, deleteType: type }));
    // note: messages stays in state for bulk delete
  };

  const handleConfirmDelete = async () => {
    const { message: msg, messages: msgs, deleteType } = deleteFlow;
    setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, messages: null, deleteType: null });
    if (!authToken) return;
    const toDelete = msgs || (msg ? [msg] : []);
    if (toDelete.length === 0) return;
    try {
      for (const m of toDelete) {
        await chatApi.deleteMessage({ token: authToken, messageId: m.id, deleteType });
        if (deleteType === 'for_me') {
          removeDmMessage(selectedFriendId, m.id);
        } else {
          updateDmMessage(selectedFriendId, m.id, {
            is_deleted_for_everyone: true,
            deleted_by_name: user?.name || user?.username || t('chatPage.youShort'),
          });
        }
      }
      if (msgs) exitSelectionMode();
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('common:errorTitle'), message: t('chatPage.deleteFailedMessage') },
      }));
    }
  };

  const handleSendImage = async (imageFile, caption) => {
    if (!selectedFriendId || !authToken || !imageFile) return;
    if (imageFile.size > maxUploadBytes) {
      showUploadToast('error', t('chatPage.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
      return;
    }
    await uploadWithType('image', imageFile, { caption });
  };

  const handleFileSelect = async (type) => {
    if (!selectedFriendId || !authToken) return;
    const friend = friends.find(f => {
      const friendId = f.email || f.id;
      return friendId === selectedFriendId && !f.isDeleted;
    });
    if (!friend) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'image'
      ? 'image/*'
      : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.txt,.jpg,.jpeg,.png,.gif,.webp,.heic';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (type === 'image') {
        if (file.size > maxUploadBytes) {
          showUploadToast('error', t('chatPage.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
          return;
        }
        setSelectedImageFile(file);
        setShowImagePreviewModal(true);
        return;
      }
      if (file.size > maxUploadBytes) {
        showUploadToast('error', t('chatPage.uploadSizeLimitTitle'), UPGRADE_MESSAGES.uploadLimit);
        return;
      }
      setPendingFile(file);
      setShowFileConfirmModal(true);
    };
    input.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (!friendExists) return;
    setIsDraggingOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (!friendExists || !selectedFriendId || !authToken) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'];
    if (file.type.startsWith('image/') || imageExts.includes(ext)) {
      setSelectedImageFile(file);
      setShowImagePreviewModal(true);
    } else {
      setPendingFile(file);
      setShowFileConfirmModal(true);
    }
  };

  const handleDeleteMessages = () => {
    if (selectedFriendId) {
      deleteMessages(selectedFriendId);
      setShowDeleteModal(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleSearchNext = () => {
    if (searchMatches.length === 0) return;
    setSearchMatchIndex((prev) => (prev + 1) % searchMatches.length);
  };

  const handleSearchPrevious = () => {
    if (searchMatches.length === 0) return;
    setSearchMatchIndex((prev) => (prev - 1 + searchMatches.length) % searchMatches.length);
  };

  // Determine if we're in group chat or direct chat mode
  const isGroupChat = !!selectedGroupId;
  const isDirectChat = !!selectedFriendId && !isGroupChat;
  
  // GROUP CHAT MODE - Render group chat UI (check selectedGroupId first, then selectedGroup)
  if (isGroupChat) {
    // If group is being loaded, show loading state
    if (!selectedGroup) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="h-full flex items-center justify-center bg-gray-50 dark:bg-[var(--color-bg-base)]"
        >
          <div className="text-center text-gray-600 dark:text-[var(--color-text-secondary)]">
            <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center mb-6 mx-auto border border-slate-200 dark:border-white/10">
              <Users className="w-10 h-10 text-slate-900 dark:text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-gray-800 dark:text-white">{t('chatPage.groupLoadingTitle')}</h2>
            <p className="text-sm text-gray-600 dark:dark:text-[var(--color-text-secondary)]">{t('chatPage.pleaseWait')}</p>
          </div>
        </motion.div>
      );
    }
    
    // Three-column layout for group chat
    return (
      <div className="h-full flex overflow-hidden bg-gray-50 dark:bg-[var(--color-bg-base)]">
        {/* Left Panel - Chat List (already handled by ChatSidebar in Layout) */}
        
        {/* Middle Panel - Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <GroupChatView
            group={selectedGroup}
            onClose={() => selectGroup(null)}
            onShowGroupInfo={() => setShowGroupInfoSidebar(!showGroupInfoSidebar)}
            onContactOpenFriendInfo={(friendId) => {
              selectGroup(null);
              selectFriend(friendId);
              setShowFriendInfoSidebar(true);
            }}
            onContactAddFriend={async (email) => {
              try {
                await chatApi.sendFriendRequest({ token: authToken, receiverEmail: email });
                window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                  detail: { type: 'success', title: t('chatPage.friendRequestSentTitle'), message: t('chatPage.friendRequestSentMessage') },
                }));
                loadPendingFriendRequestCount?.();
              } catch (e) {
                window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                  detail: { type: 'error', title: t('common:errorTitle'), message: e?.payload?.message || e?.message || t('chatPage.friendRequestErrorFallback') },
                }));
              }
            }}
          />
        </div>

        {/* Right Panel - Group Info Sidebar */}
        {showGroupInfoSidebar && selectedGroup && (
          <GroupInfoSidebar
            group={selectedGroup}
            isOpen={showGroupInfoSidebar}
            onClose={() => setShowGroupInfoSidebar(false)}
          />
        )}
      </div>
    );
  }

  // DIRECT CHAT MODE - Render direct chat UI
  // Default state - No friend or group selected
  if (!selectedFriendId && !selectedGroupId) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="h-full flex items-center justify-center"
      >
        <div className="text-center text-gray-600 dark:text-[var(--color-text-secondary)]">
          <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center mb-6 mx-auto border border-slate-200 dark:border-white/10">
            <MessageCircle className="w-10 h-10 text-slate-900 dark:text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-800 dark:text-gray-300">{t('chatPage.selectFriendOrGroupTitle')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-500">{t('chatPage.selectFriendOrGroupSubtitle')}</p>
        </div>
      </motion.div>
    );
  }

  // If friend is selected but not loaded yet, show loading
  if (selectedFriendId && !selectedFriend) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="h-full flex items-center justify-center"
      >
        <div className="text-center text-gray-600 dark:text-[var(--color-text-secondary)]">
          <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center mb-6 mx-auto border border-slate-200 dark:border-white/10">
            <MessageCircle className="w-10 h-10 text-slate-900 dark:text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-800 dark:text-white">{t('chatPage.chatLoadingTitle')}</h2>
          <p className="text-sm text-gray-600 dark:text-[var(--color-text-secondary)]">{t('chatPage.pleaseWait')}</p>
        </div>
      </motion.div>
    );
  }

  // If selectedFriend is null but selectedFriendId exists, don't render (should show loading above)
  if (!selectedFriend && selectedFriendId) {
    return null;
  }

  // Check if selected friend still exists in friends list and is not deleted
  // isDeleted flag means friend was removed but messages are kept for history
  const friendExists = selectedFriend && 
    friends.find(f => {
      const friendId = f.email || f.id;
      const selectedId = selectedFriend.email || selectedFriend.id;
      return friendId === selectedId && !f.isDeleted;
    });

  // Three-column layout for direct chat
  if (selectedFriend) {
    return (
      <div className="h-full flex overflow-hidden bg-gray-50 dark:bg-[var(--color-bg-base)]">
        {/* Left Panel - Chat List (already handled by ChatSidebar in Layout) */}
        
        {/* Middle Panel - Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div
            className="h-full flex flex-col overflow-hidden bg-gray-50 dark:bg-[var(--color-bg-base)] relative"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Drag overlay - WhatsApp style */}
            <AnimatePresence>
              {isDraggingOver && friendExists && !showImagePreviewModal && !showFileConfirmModal && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 z-[100005] flex items-center justify-center rounded-xl"
 style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                >
                  <div className="text-center text-white drop-shadow-lg">
                    <p className="text-lg font-medium">{t('chatPage.dropFileHere')}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/10 bg-white/90 dark:bg-[var(--color-bg-base)] backdrop-blur-sm">
              <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => setShowFriendInfoSidebar(!showFriendInfoSidebar)}>
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center text-sm font-semibold text-slate-900 dark:text-white border border-slate-200 dark:border-white/10">
                    {(selectedFriend.avatarUrl || selectedFriend.avatar_url) ? (
                      <img
                        src={selectedFriend.avatarUrl || selectedFriend.avatar_url}
                        alt={getDisplayName(selectedFriend)}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getInitials(getDisplayName(selectedFriend))
                    )}
                  </div>
                  {/* Status Indicator - WhatsApp-like: green for online, gray for offline */}
                  <div
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-[#0d1424] ${
                      selectedFriend.status === 'online' || selectedFriend.is_online === true ? 'bg-green-500' : 
                      'bg-gray-400 dark:bg-gray-600'
                    }`}
                  />
                </div>
                <FriendStatusDisplay
                  friend={selectedFriend}
                  isTyping={isTyping}
                  onFriendClick={() => setShowFriendInfoSidebar(!showFriendInfoSidebar)}
                />
              </div>
              
              {/* Header Actions - Search and Delete */}
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">

          {/* Seçim modu: Kapat + İlet + Sil */}
          {isSelectionMode && (
            <motion.button
              whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              onClick={exitSelectionMode}
              className="p-2 rounded-xl hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] transition-colors"
              title={t('chatPage.closeSelection')}
            >
              <X className="w-5 h-5" />
            </motion.button>
          )}

          {/* Seçim modu: İlet + Sil */}
          {isSelectionMode && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={handleSelectionForward}
                disabled={selectedMessageIds.size === 0}
                className="p-2 rounded-xl hover:bg-[var(--color-bg-elevated)] text-[#3d8ef0] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={t('chatPage.forwardSelected')}
              >
                <Share2 className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={handleSelectionDelete}
                disabled={selectedMessageIds.size === 0}
                className="p-2 rounded-xl hover:bg-red-500/10 text-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={t('chatPage.deleteSelected')}
              >
                <Trash2 className="w-5 h-5" />
              </motion.button>
              <div className="w-px h-5 bg-white/10 mx-1" />
            </>
          )}

          {/* Message Search */}
          {showSearchBar ? (
            <div className="flex items-center gap-2 bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl px-3 py-2 border border-slate-200 dark:border-white/10">
              <Search className="w-4 h-4 text-gray-500 dark:text-[var(--color-text-secondary)] flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={messageSearchQuery}
                onChange={(e) => setMessageSearchQuery(e.target.value)}
                placeholder={t('chatPage.searchPlaceholder')}
                className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#c8d8eb] min-w-0"
              />
              {messageSearchQuery.trim() && searchMatches.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{searchMatchIndex + 1}/{searchMatches.length}</span>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={handleSearchPrevious}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)]"
                      title={t('chatPage.previousMatch')}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={handleSearchNext}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)]"
                      title={t('chatPage.nextMatch')}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
              <button
                onClick={handleSearchClose}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-500 dark:text-[var(--color-text-secondary)]"
                title={t('chatPage.closeSearch')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowSearchBar(true)}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            title={t('chatPage.searchMessages')}
          >
            <Search className="w-5 h-5" />
          </motion.button>
          )}
          
          {/* Info Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowFriendInfoSidebar(!showFriendInfoSidebar)}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            title={t('chatPage.friendInfoTitle')}
          >
            <Info className="w-5 h-5" />
          </motion.button>
          
          {/* Close Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleCloseChat}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            title={t('chatPage.closeChat')}
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>
      </div>

            {/* Messages List — flex-col-reverse + overflow-anchor: sessiz sync'te scroll sabit kalır */}
            <div 
              ref={messagesContainerRef} 
              className="chat-scroll-area flex-1 overflow-y-auto p-4 flex flex-col-reverse gap-4 relative z-0 allow-text-selection bg-gray-50 dark:bg-[var(--color-bg-base)]"
              style={{ overflowAnchor: 'none' }}
              onClick={() => {
                if (selectedFriendId) markMessagesAsRead();
              }}
            >
        {/* Çapa: column-reverse'te ilk child = görsel en alt; yeni mesajlar eklendiğinde scroll sabit kalır */}
        <div style={{ overflowAnchor: 'auto', height: 1, minHeight: 1 }} aria-hidden="true" />
        <AnimatePresence>
          {loadingMessagesForFriendId === selectedFriendId && (!selectedFriendMessages || selectedFriendMessages.length === 0) ? (
            <div className="h-full flex items-center justify-center min-h-[200px]">
              <div className="text-center text-gray-500 dark:text-[var(--color-text-secondary)] flex flex-col items-center gap-3">
                <Loader className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
                <p className="text-sm font-medium">{t('chatPage.syncingMessages')}</p>
              </div>
            </div>
          ) : selectedFriendMessages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500 dark:text-[var(--color-text-secondary)]">
                <p className="text-sm">{t('chatPage.noMessagesYet')}</p>
                <p className="text-xs mt-1">{t('chatPage.writeFirstMessage')}</p>
              </div>
            </div>
          ) : (
            [...filteredMessages].reverse().map((message) => {
              const isOwnMessage = message.senderId === 'current_user';
              const messageDate = new Date(message.timestamp);
              const isSearchMatch = searchMatchIndex >= 0 && 
                searchMatches.length > 0 &&
                searchMatches[searchMatchIndex]?.message.id === message.id;

              return (
                <motion.div
                  key={message.id}
                  data-message-id={message.id}
                  ref={(el) => {
                    if (el) messageRefs.current[message.id] = el;
                  }}
                  initial={
                    isFirstOpenForChat ||
                    snap.ids.has(String(message.id)) ||
                    noAnimIdsRef.current.has(String(message.id))
                      ? false
                      : { opacity: 0, y: 8 }
                  }
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className={`flex items-center gap-2 ${isOwnMessage ? 'justify-end' : 'justify-start'} ${isSelectionMode ? 'cursor-pointer select-none' : ''}`}
                  style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
                  onClick={isSelectionMode ? () => toggleMessageSelection(message.id) : undefined}
                >
                  {/* Seçim modu checkbox */}
                  {isSelectionMode && (
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      selectedMessageIds.has(message.id)
                        ? 'bg-[#2563eb] border-[#3d8ef0]'
                        : 'border-white/30 bg-transparent'
                    } ${isOwnMessage ? 'order-last' : 'order-first'}`}>
                      {selectedMessageIds.has(message.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  )}
                  <div className={`flex gap-2 ${message.type === 'poll' || message.type === 'contact' ? 'max-w-[85%]' : 'max-w-[70%]'} ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                    {!isOwnMessage && (
                      <div className="message-avatar w-8 h-8 rounded-full bg-slate-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center text-xs font-semibold text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 flex-shrink-0">
                        {selectedFriend.avatarUrl ? (
                          <img
                            src={selectedFriend.avatarUrl}
                            alt={getDisplayName(selectedFriend)}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(getDisplayName(selectedFriend))
                        )}
                      </div>
                    )}
                    <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                      {/* Forwarded Label (if message is forwarded AND not from current user) */}
                      {(() => {
                        // Only show "İletildi" label if:
                        // 1. Message is forwarded
                        // 2. Original sender is NOT the current user (user forwarding someone else's message)
                        
                        // Check both camelCase and snake_case formats
                        const isForwarded = message.isForwarded === true || 
                                          message.is_forwarded === true || 
                                          message.is_forwarded === 1;
                        
                        // Get forwardedFromSenderId from either format
                        const forwardedFromSenderId = message.forwardedFromSenderId || 
                                                    message.forwarded_from_sender_id || 
                                                    null;
                        
                        // Normalize IDs to strings for comparison
                        const forwardedFromSenderIdStr = forwardedFromSenderId ? String(forwardedFromSenderId) : null;
                        const currentUserIdStr = user?.id ? String(user.id) : null;
                        
                        const shouldShowForwardedLabel = 
                          isForwarded && 
                          forwardedFromSenderIdStr && 
                          currentUserIdStr &&
                          forwardedFromSenderIdStr !== currentUserIdStr;
                        
                        return shouldShowForwardedLabel ? (
                          <div className="mb-1 px-2 py-1 text-xs text-gray-600 dark:text-[var(--color-text-secondary)] flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" />
                            <span>{t('chatPage.forwardedLabel')}</span>
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Reply Preview (if message has replyTo) */}
                      {message.replyTo && (
                        <div
                          className={`mb-1 px-3 py-2 bg-gray-100 dark:bg-[var(--color-bg-elevated)] rounded-lg border-l-4 ${
                            isOwnMessage 
                              ? 'border-slate-300 dark:border-white/20' 
                              : 'border-gray-400 dark:border-gray-500'
                          } max-w-[85%] cursor-pointer hover:opacity-80 transition-opacity`}
                          onClick={() => {
                            // Scroll to replied message
                            const repliedMessageElement = messageRefs.current[message.replyTo.id];
                            if (repliedMessageElement) {
                              repliedMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              // Highlight the message briefly
                              repliedMessageElement.style.backgroundColor = 'var(--color-surface-hover)';
                              setTimeout(() => {
                                repliedMessageElement.style.backgroundColor = '';
                                repliedMessageElement.style.transition = 'background-color 0.5s ease';
                              }, 1500);
                            }
                          }}
                        >
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-0.5">
                            {(() => {
                              // Check if reply sender is Plus member
                              const replySenderIsPlus = selectedFriend?.isPlus || selectedFriend?.role === 'plus' || selectedFriend?.role === 'admin';
                              if (replySenderIsPlus) {
                                return (
                                  <Username user={selectedFriend} variant="subtle" />
                                );
                              }
                              return <span>{message.replyTo.senderName || t('chatPage.unknownFallback')}</span>;
                            })()}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-[var(--color-text-secondary)] line-clamp-2 break-words">
                            {message.replyTo.text || t('chatPage.deletedMessageFallback')}
                          </p>
                        </div>
                      )}
                      
                      {/* Deleted for everyone — placeholder */}
                      {message.is_deleted_for_everyone && (
                        <div className="flex items-center gap-2 py-1 px-1 my-0.5 select-none">
                          <div className="flex-1 h-px bg-white/[0.08]" />
                          <span className="text-[11px] italic text-[#4d6a8a] whitespace-nowrap">
                            {t('chatPage.deletedForEveryone', { name: message.deleted_by_name })}
                          </span>
                          <div className="flex-1 h-px bg-white/[0.08]" />
                        </div>
                      )}

                      {!message.is_deleted_for_everyone && (() => {
                        const isCardType = ['note_share', 'note_collab_invite', 'event_share', 'event_cancelled', 'poll', 'contact', 'image', 'file'].includes(message.type);
                        const isEmojiOnly = message.type === 'text' && isEmojiOnlyContent(message.content);
                        return (
                      <div
                        data-message-bubble
                        className={`message-bubble rounded-2xl ${
                          isCardType || isEmojiOnly
                            ? (isEmojiOnly ? 'px-1 py-0.5' : '')
                            : `px-4 py-2.5 ${isOwnMessage
                              ? 'rounded-br-sm bg-blue-100 dark:bg-[#1d3557] text-slate-900 dark:text-white'
                              : 'rounded-bl-sm bg-slate-100 dark:bg-[#060c18] text-slate-900 dark:text-white border border-slate-300/80 dark:border-slate-700/60'}`
                        } ${
                          isSearchMatch ? 'ring-2 ring-yellow-400 ring-offset-2 dark:ring-offset-[#0d1424]' : ''
                        }`}
                        onDoubleClick={(e) => {
                          if (isSelectionMode) return;
                          e.preventDefault();
                          replyToMsg(message);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isSelectionMode) return;
                          setMessageContextMenu({
                            isOpen: true,
                            x: e.clientX,
                            y: e.clientY,
                            message: message,
                          });
                        }}
                      >
                        {message.type === 'text' && (
                          <p
                            className={`message-text ${isEmojiOnly ? 'text-[2.5rem] leading-tight' : `text-sm ${isOwnMessage ? 'text-slate-900 dark:text-white' : 'text-gray-900 dark:text-white'}`} whitespace-pre-wrap break-words`}
                          >
                            {renderMessageContent(message.content, messageSearchQuery, isOwnMessage)}
                          </p>
                        )}
                        {message.type === 'image' && (message.file_url || message.fileUrl) && (
                          <div className="space-y-2">
                            <ChatImage
                              fileUrl={message.fileUrl || message.file_url}
                              onClick={() => {
                                setSelectedImageUrl(message.fileUrl || message.file_url);
                                setSelectedImageFileName(message.file_name || message.fileName || 'image');
                                setShowImageModal(true);
                              }}
                            />
                            {/* Show caption if exists and is not just the filename */}
                            {message.content && 
                             message.content.trim() && 
                             message.content !== message.file_name && (
                              <p className={`message-text text-sm ${isOwnMessage ? 'text-slate-900 dark:text-white' : 'text-gray-900 dark:text-white'} whitespace-pre-wrap break-words`}>
                                {renderMessageContent(message.content, messageSearchQuery, isOwnMessage)}
                              </p>
                            )}
                          </div>
                        )}
                        {message.type === 'file' && message.file_url && (
                          <div className="mt-2 flex items-center gap-2 p-2 bg-gray-100 dark:bg-[var(--color-bg-elevated)] rounded-lg">
                            <Paperclip className="w-4 h-4 text-gray-500 dark:text-[var(--color-text-secondary)]" />
                            <button
                              onClick={() => handleFileDownload(message.file_url, message.file_name || message.content)}
                              className="text-sm font-medium text-slate-900 dark:text-white hover:underline truncate text-left"
                            >
                              {fixFileName(message.file_name || message.content || t('chatPage.fileDefaultName'))}
                            </button>
                          </div>
                        )}
                        {message.type === 'note_share' && (
                          <SharedNoteMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            groupId={null}
                          />
                        )}
                        {message.type === 'note_collab_invite' && (
                          <CollabInviteMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            groupId={null}
                            onAccepted={() => {}}
                          />
                        )}
                        {(message.type === 'event_share' || message.type === 'event_cancelled') && (
                          <EventCardMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            messageId={message.id}
                            chatType="dm"
                            friendId={selectedFriendId}
                          />
                        )}
                        {message.type === 'poll' && (
                          <PollCardMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            messageId={message.id}
                            chatType="dm"
                            friendId={selectedFriendId}
                          />
                        )}
                        {message.type === 'contact' && (
                          <ContactCardMessage
                            message={message}
                            isOwnMessage={isOwnMessage}
                            onOpenFriendInfo={(friendId) => {
                              selectGroup(null);
                              selectFriend(friendId);
                              setShowFriendInfoSidebar(true);
                            }}
                            onAddFriend={async (email) => {
                              try {
                                await chatApi.sendFriendRequest({ token: authToken, receiverEmail: email });
                                window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                                  detail: { type: 'success', title: t('chatPage.friendRequestSentTitle'), message: t('chatPage.friendRequestSentMessage') },
                                }));
                                loadPendingFriendRequestCount?.();
                              } catch (e) {
                                window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                                  detail: { type: 'error', title: t('common:errorTitle'), message: e?.payload?.message || e?.message || t('chatPage.friendRequestErrorFallback') },
                                }));
                              }
                            }}
                          />
                        )}
                      </div>
                        );
                      })()}
                      {/* Reactions */}
                      {!message.is_deleted_for_everyone && messageReactions[message.id] && Object.keys(messageReactions[message.id]).length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-1.5 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          {Object.entries(messageReactions[message.id]).map(([emoji, count]) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => handleAddReaction(emoji, message.id)}
                              className="flex items-center gap-1 bg-[#161f31] hover:bg-[#24354f] border border-white/[0.1] rounded-full px-2 py-0.5 transition-colors"
                            >
                              <TwemojiText text={emoji} className="text-base" />
                              <span className="text-[var(--color-text-secondary)] text-xs ml-0.5">{count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="message-meta flex items-center gap-1 mt-1 px-1">
                        <span className="message-time text-xs text-gray-500 dark:text-[var(--color-text-secondary)]">
                          {formatMessageTime(message.timestamp)}
                        </span>
                        {/* Tick indicators - only for own messages - WhatsApp style */}
                        {isOwnMessage && (
                          <div
                            className={`message-ticks ${
                              message.read_at
                                ? 'double read'
                                : message.delivered_at
                                ? 'double'
                                : 'single'
                            }`}
                          >
                            {(message.read_at || message.delivered_at) && (
                              <span className="tick tick-back">✓</span>
                            )}
                            <span className="tick tick-front">✓</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

            {/* Upload progress bar - above input */}
            {uploadProgress != null && (
              <div className="px-4 pt-2 z-10">
                <div className="h-1.5 w-full bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-blue-500 dark:bg-blue-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${uploadProgress}%` }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
              </div>
            )}
            {/* Message Input Area */}
            <div className="relative p-4 border-t border-slate-200 dark:border-white/10 bg-white/90 dark:bg-[var(--color-bg-base)] backdrop-blur-sm z-10">
        {!friendExists && selectedFriend && (
          <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-xl">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <span className="font-semibold">{t('chatPage.notFriendWarningBold')}</span>{t('chatPage.notFriendWarningRest')}
            </p>
          </div>
        )}
        
        {/* Reply Preview */}
        {replyToMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-3 bg-slate-100 dark:bg-[var(--color-bg-elevated)] border-l-4 border-slate-300 dark:border-white/20 rounded-lg flex items-start gap-3"
          >
            <Reply className="w-4 h-4 text-gray-500 dark:text-[var(--color-text-secondary)] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-700 dark:text-white mb-1">
                {replyToMessage.sender ? (
                  <Username user={replyToMessage.sender} variant="subtle" />
                ) : (
                  replyToMessage.senderName
                )}
              </p>
              <p className="text-sm text-gray-600 dark:text-[var(--color-text-secondary)] line-clamp-2 break-words">
                {replyToMessage.text}
              </p>
            </div>
            <button
              onClick={() => setReplyToMessage(null)}
              className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-[var(--color-bg-elevated)] text-gray-500 dark:text-[var(--color-text-secondary)] hover:text-gray-700 dark:hover:text-white transition-colors"
              title={t('chatPage.cancelReply')}
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
        
        {/* Seçim modu eylem çubuğu */}
        {isSelectionMode && (
          <div className="flex items-center justify-between gap-3 px-2 py-2.5 bg-[var(--color-bg-elevated)] rounded-xl border border-white/10">
            <div className="flex items-center gap-2">
              <button
                onClick={exitSelectionMode}
                className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:bg-white/[0.05] hover:text-[var(--color-text-secondary)] transition-colors"
                title={t('chatPage.cancel')}
              >
                <X className="w-4 h-4" />
              </button>
              <span className="text-sm text-[var(--color-text-secondary)] font-medium">
                {t('chatPage.messagesSelectedCount', { count: selectedMessageIds.size })}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={copySelectedMessages}
                disabled={selectedMessageIds.size === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-[var(--color-text-secondary)] hover:bg-white/[0.05] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={t('chatPage.copy')}
              >
                <Copy className="w-3.5 h-3.5" />
                {t('chatPage.copy')}
              </button>
            </div>
          </div>
        )}

        {!isSelectionMode && (
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          {/* Action Buttons */}
          <div className="flex items-center gap-1">
            <EmojiPickerButton
              onEmojiSelect={handleEmojiSelect}
              disabled={!friendExists}
              className="p-2.5 rounded-xl hover:bg-[#1e2d47] text-[#7a96b0] hover:text-white"
            />
            <motion.button
              type="button"
              whileHover={friendExists ? { scale: 1.1 } : {}}
              whileTap={friendExists ? { scale: 0.9 } : {}}
              onClick={() => handleFileSelect('image')}
              disabled={!friendExists}
              className={`p-2.5 rounded-xl transition-colors ${
                friendExists
                  ? 'hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white'
                  : 'opacity-50 cursor-not-allowed text-gray-400 dark:text-[var(--color-text-secondary)]'
              }`}
              title={friendExists ? t('chatPage.photoTitle') : t('chatPage.notFriendFileTitle')}
            >
              <Image className="w-5 h-5" />
            </motion.button>
            <div ref={attachmentTriggerRef} data-chat-attach-trigger="true">
              <motion.button
                type="button"
                whileHover={friendExists ? { scale: 1.1 } : {}}
                whileTap={friendExists ? { scale: 0.9 } : {}}
                onClick={() => setShowAttachmentPopover((v) => !v)}
                disabled={!friendExists}
                className={`p-2.5 rounded-xl transition-colors ${
                  friendExists
                    ? 'hover:bg-gray-100 dark:hover:bg-[var(--color-bg-elevated)] text-gray-600 dark:text-[var(--color-text-secondary)] hover:text-gray-900 dark:hover:text-white'
                    : 'opacity-50 cursor-not-allowed text-gray-400 dark:text-[var(--color-text-secondary)]'
                } ${showAttachmentPopover ? 'bg-gray-100 dark:bg-[var(--color-bg-elevated)]' : ''}`}
                title={friendExists ? t('chatPage.attachmentsTitle') : t('chatPage.notFriendAttachTitle')}
              >
                <Plus className="w-5 h-5" />
              </motion.button>
            </div>
            <ChatAttachmentPopover
              isOpen={showAttachmentPopover}
              onClose={() => setShowAttachmentPopover(false)}
              anchorRef={attachmentTriggerRef}
              disabled={!friendExists}
              onShareNote={() => setShowNoteListModal(true)}
              onSharePhoto={() => handleFileSelect('image')}
              onShareFile={() => handleFileSelect('file')}
              onCreateEvent={() => {
                setShowAttachmentPopover(false);
                const count = calendars?.length ?? 0;
                if (count === 0) {
                  window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                    detail: { type: 'info', title: t('chatPage.calendarRequiredTitle'), message: t('chatPage.calendarRequiredMessage') },
                  }));
                } else if (count === 1) {
                  setShowCreateEventModal(true);
                } else {
                  setShowSelectCalendarModal(true);
                }
              }}
              onCreatePoll={() => {
                setShowAttachmentPopover(false);
                setShowPollCreateModal(true);
              }}
              onShareContact={() => {
                setShowAttachmentPopover(false);
                setShowContactShareModal(true);
              }}
            />
          </div>

          {/* Text Input */}
          <div
            className="flex-1 relative min-w-0"
            onContextMenu={(e) => {
              if (!friendExists) return;
              e.preventDefault();
              e.stopPropagation();
              const hasSelectedText = window.getSelection()?.toString() !== '';
              setTextAreaContextMenu({
                isOpen: true,
                x: e.clientX,
                y: e.clientY,
                hasSelection: hasSelectedText,
              });
            }}
            onFocus={() => {
              if (!hasMarkedAsReadOnFocusRef.current) {
                markMessagesAsRead();
                hasMarkedAsReadOnFocusRef.current = true;
              }
            }}
          >
            <TwemojiInput
              ref={inputRef}
              onTextChange={(newValue) => {
                setMessageText(newValue);
                setIsComposing(newValue.trim().length > 0);

                if (!friendExists || !selectedFriendId) return;
                const socket = getSocket();
                if (!socket || !socket.connected) return;
                if (newValue.trim().length > 0) markMessagesAsRead();
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                if (!isTypingActiveRef.current && newValue.trim().length > 0) {
                  socket.emit('typing:start', { receiver_id: selectedFriendId });
                  isTypingActiveRef.current = true;
                }
                typingTimeoutRef.current = setTimeout(() => {
                  if (isTypingActiveRef.current) {
                    socket.emit('typing:stop', { receiver_id: selectedFriendId });
                    isTypingActiveRef.current = false;
                  }
                  typingTimeoutRef.current = null;
                }, 2000);
              }}
              onEnterPress={handleSendMessage}
              placeholder={friendExists ? t('chatPage.placeholderDefault') : t('chatPage.placeholderNotFriend')}
              className={`!rounded-xl !bg-[#161f31] !border-white/10 !min-h-[44px] !max-h-[80px] !py-3${!friendExists ? ' opacity-50 pointer-events-none' : ''}`}
            />
          </div>

          {/* Send Button */}
          <motion.button
            type="submit"
            whileHover={friendExists && messageText.trim() ? { scale: 1.05 } : {}}
            whileTap={friendExists && messageText.trim() ? { scale: 0.95 } : {}}
            disabled={!messageText.trim() || !friendExists}
            className={`flex-shrink-0 p-3 rounded-xl transition-all duration-200 ${
              friendExists && messageText.trim()
                ? 'bg-slate-100 dark:bg-[var(--color-bg-elevated)] text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-[#24354f]'
                : 'bg-gray-200 dark:bg-[var(--color-bg-elevated)] text-gray-400 dark:text-[var(--color-text-secondary)] cursor-not-allowed'
            }`}
            title={friendExists ? t('chatPage.sendButtonTitle') : t('chatPage.sendButtonNotFriendTitle')}
          >
            <Send className="w-5 h-5" />
          </motion.button>
        </form>
        )}
      </div>

      {/* Delete Messages Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteMessages}
        title={t('chatPage.deleteMessagesModalTitle')}
        message={t('chatPage.deleteMessagesModalMessage', { name: selectedFriend?.name })}
      />

      {/* Delete message — choose type modal */}
      <AnimatePresence>
        {deleteFlow.chooseOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
              onClick={() => setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null })}
            />
            <motion.div className="fixed inset-0 flex items-center justify-center z-[100001] p-4" onClick={(e) => e.stopPropagation()}>
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 8 }}
                transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                className="w-full max-w-sm bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 p-6 pb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-red-400" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{t('chatPage.deleteChoice.title')}</h3>
                </div>
                <div className="px-6 pb-6 space-y-2">
                  <p className="text-sm text-[var(--color-text-muted)] mb-4">{t('chatPage.deleteChoice.question')}</p>
                  {(deleteFlow.message?.senderId === 'current_user' || (deleteFlow.messages && deleteFlow.messages.every(m => m.senderId === 'current_user'))) && (
                    <button
                      onClick={() => chooseDeleteType('for_everyone')}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-sm font-medium transition-colors text-left"
                    >
                      <Trash2 className="w-4 h-4 flex-shrink-0" />
                      <div>
                        <div className="font-semibold">{t('chatPage.deleteChoice.forEveryone')}</div>
                        <div className="text-xs text-red-400/70 mt-0.5">{t('chatPage.deleteChoice.forEveryoneHint')}</div>
                      </div>
                    </button>
                  )}
                  <button
                    onClick={() => chooseDeleteType('for_me')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/[0.08] text-[var(--color-text-secondary)] text-sm font-medium transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">{t('chatPage.deleteChoice.forMe')}</div>
                      <div className="text-xs text-[#4d6a8a] mt-0.5">{t('chatPage.deleteChoice.forMeHint')}</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null })}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-[var(--color-text-muted)] hover:bg-white/[0.04] transition-colors mt-1"
                  >
                    {t('chatPage.deleteChoice.cancel')}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete message — confirm modal */}
      <DeleteModal
        isOpen={deleteFlow.confirmOpen}
        onClose={() => setDeleteFlow({ chooseOpen: false, confirmOpen: false, message: null, deleteType: null })}
        onConfirm={handleConfirmDelete}
        title={deleteFlow.deleteType === 'for_everyone' ? t('chatPage.deleteConfirm.forEveryoneTitle') : t('chatPage.deleteConfirm.forMeTitle')}
        message={
          deleteFlow.deleteType === 'for_everyone'
            ? (deleteFlow.messages
                ? t('chatPage.deleteConfirm.forEveryoneMessagePlural', { count: deleteFlow.messages.length })
                : t('chatPage.deleteConfirm.forEveryoneMessageSingle'))
            : (deleteFlow.messages
                ? t('chatPage.deleteConfirm.forMeMessagePlural', { count: deleteFlow.messages.length })
                : t('chatPage.deleteConfirm.forMeMessageSingle'))
        }
      />

      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImageUrl(null);
          setSelectedImageFileName(null);
        }}
        imageUrl={selectedImageUrl}
        fileName={selectedImageFileName}
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={showImagePreviewModal}
        onClose={() => {
          setShowImagePreviewModal(false);
          setSelectedImageFile(null);
        }}
        imageFile={selectedImageFile}
        onSend={handleSendImage}
      />

      {/* File Confirm Modal - non-image file drop / Dosya Paylaş */}
      <FileConfirmModal
        isOpen={showFileConfirmModal}
        onClose={() => {
          setShowFileConfirmModal(false);
          setPendingFile(null);
        }}
        file={pendingFile}
        onConfirm={async (file) => {
          await uploadWithType('file', file);
          setShowFileConfirmModal(false);
          setPendingFile(null);
        }}
        onCancel={() => {
          setShowFileConfirmModal(false);
          setPendingFile(null);
        }}
      />

      {/* Message Info Modal */}
      <MessageInfoModal
        isOpen={showMessageInfoModal}
        onClose={() => {
          setShowMessageInfoModal(false);
          setSelectedMessageForInfo(null);
        }}
        message={selectedMessageForInfo}
      />

      {/* Forward Modal */}
      <ForwardModal
        isOpen={showForwardModal}
        onClose={() => {
          setShowForwardModal(false);
          setForwardMessage(null);
          setSelectionForwardMessages(null);
        }}
        message={selectionForwardMessages ? null : forwardMessage}
        messages={selectionForwardMessages}
      />

      {/* Note List Modal (Share Note to Chat) */}
      <NoteListModal
        isOpen={showNoteListModal}
        onClose={() => setShowNoteListModal(false)}
        shareTarget={selectedFriendId && selectedFriend ? { type: 'friend', friendEmail: selectedFriend.email || selectedFriendId } : null}
        onShareSuccess={() => {
          if (selectedFriendId) {
            reloadMessages(selectedFriendId);
            loadUnreadCount();
          }
        }}
      />

      <SelectCalendarModal
        isOpen={showSelectCalendarModal}
        onClose={() => setShowSelectCalendarModal(false)}
        calendars={calendars || []}
        onSelect={(cal) => {
          setSelectedCalendarForEvent(cal);
          setShowSelectCalendarModal(false);
          setShowCreateEventModal(true);
        }}
      />
      <CreateEventModal
        isOpen={showCreateEventModal}
        onClose={() => { setShowCreateEventModal(false); setSelectedCalendarForEvent(null); }}
        calendar={selectedCalendarForEvent || selectedCalendar || calendars?.[0]}
        onCreate={async (eventData) => {
          try {
            const savedEvent = await createEvent(eventData);
            if (!savedEvent) return;
            const payload = {
              event_id: savedEvent.id,
              event_title: savedEvent.title,
              event_start: savedEvent.startDate,
              event_end: savedEvent.endDate,
              event_all_day: savedEvent.allDay ?? false,
              event_description: savedEvent.description ?? null,
              event_location: eventData.location ?? null,
            };
            sendMessage(selectedFriendId, '', 'event_share', null, null, null, null, payload);
            setShowCreateEventModal(false);
          } catch (err) {
            console.error('[ChatPage] Create event failed:', err);
            window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
              detail: { type: 'error', title: t('chatPage.eventCreateFailedTitle'), message: getApiErrorMessage(err, { fallback: t('chatPage.eventCreateFailedFallback') }) },
            }));
          }
        }}
      />

      <PollCreateModal
        isOpen={showPollCreateModal}
        onClose={() => setShowPollCreateModal(false)}
        onSubmit={(payload) => {
          sendMessage(selectedFriendId, '', 'poll', null, null, null, null, null, payload);
          setShowPollCreateModal(false);
        }}
      />

      <ContactShareModal
        isOpen={showContactShareModal}
        onClose={() => setShowContactShareModal(false)}
        excludeFriendId={selectedFriendId}
        onSubmit={(payload) => {
          sendMessage(selectedFriendId, '', 'contact', null, null, null, null, null, null, payload);
          setShowContactShareModal(false);
        }}
      />

      {/* Textarea Context Menu */}
      <ContextMenu
        isOpen={textAreaContextMenu.isOpen}
        position={{ x: textAreaContextMenu.x, y: textAreaContextMenu.y }}
        items={[
          {
            label: t('chatPage.contextMenu.cut'),
            icon: Scissors,
            onClick: handleCut,
            disabled: !textAreaContextMenu.hasSelection,
          },
          {
            label: t('chatPage.contextMenu.copy'),
            icon: Copy,
            onClick: handleCopy,
            disabled: !textAreaContextMenu.hasSelection,
          },
          {
            label: t('chatPage.contextMenu.paste'),
            icon: Clipboard,
            onClick: handlePaste,
          },
          {
            label: t('chatPage.contextMenu.pastePlain'),
            icon: FileText,
            onClick: handlePasteAsPlainText,
          },
          {
            label: t('chatPage.contextMenu.selectAll'),
            icon: MousePointerClick,
            onClick: handleSelectAll,
            disabled: !messageText || messageText.length === 0,
          },
        ]}
        onClose={() => setTextAreaContextMenu({ isOpen: false, x: 0, y: 0, hasSelection: false })}
      />

      {/* Message Context Menu */}
      <ContextMenu
        isOpen={messageContextMenu.isOpen}
        position={{ x: messageContextMenu.x, y: messageContextMenu.y }}
        header={
          <div className="flex items-center gap-0.5 px-2 py-2">
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                type="button"
                onClick={() => handleAddReaction(emoji)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.08] hover:scale-125 transition-all duration-150 text-lg"
              >
                <TwemojiText text={emoji} />
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                const msg = messageContextMenu.message;
                setReactionPickerState({ isOpen: true, x: messageContextMenu.x, y: messageContextMenu.y, messageId: msg?.id });
                setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null });
              }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/[0.08] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        }
        items={[
          // Mesaj bilgisi - Sadece kendi mesajlarında göster
          ...(messageContextMenu.message?.senderId === 'current_user'
            ? [
                {
                  label: t('chatPage.messageMenu.info'),
                  icon: Info,
                  onClick: handleShowMessageInfo,
                },
              ]
            : []),
          {
            label: t('chatPage.messageMenu.reply'),
            icon: Reply,
            onClick: handleReplyMessage,
          },
          {
            label: t('chatPage.messageMenu.copy'),
            icon: Copy,
            onClick: handleCopyMessage,
          },
          {
            label: t('chatPage.messageMenu.forward'),
            icon: Share2,
            onClick: handleForwardMessage,
          },
          {
            label: t('chatPage.messageMenu.select'),
            icon: CheckSquare,
            onClick: handleSelectMessage,
          },
          // Sil — silinmemiş mesajlar için
          ...(!messageContextMenu.message?.is_deleted_for_everyone
            ? [{
                label: t('chatPage.messageMenu.delete'),
                icon: Trash2,
                onClick: openDeleteFlow,
                danger: true,
              }]
            : []),
          // Mesajı Bildir — yalnız başkasının mesajı
          ...(messageContextMenu.message?.senderId !== 'current_user' &&
            !messageContextMenu.message?.is_deleted_for_everyone
            ? [{
                label: t('chatPage.messageMenu.report'),
                icon: Flag,
                onClick: () => {
                  const msg = messageContextMenu.message;
                  setReportModal({ isOpen: true, messageId: msg?.id || null });
                },
              }]
            : []),
        ]}
        onClose={() => setMessageContextMenu({ isOpen: false, x: 0, y: 0, message: null })}
      />

      <ReportMessageModal
        isOpen={reportModal.isOpen}
        messageId={reportModal.messageId}
        onClose={() => setReportModal({ isOpen: false, messageId: null })}
      />

      {/* Reaction full emoji picker */}
      {reactionPickerState.isOpen && (
        <ReactionEmojiPicker
          x={Math.min(reactionPickerState.x, window.innerWidth - 360)}
          y={Math.min(reactionPickerState.y, window.innerHeight - 420)}
          messageId={reactionPickerState.messageId}
          onSelect={(emoji, msgId) => handleAddReaction(emoji, msgId)}
          onClose={() => setReactionPickerState({ isOpen: false, x: 0, y: 0, messageId: null })}
        />
      )}

          </div>
          
          {/* Right Panel - Friend Info Sidebar */}
          {showFriendInfoSidebar && selectedFriend && (
            <FriendInfoSidebar
              friend={selectedFriend}
              isOpen={showFriendInfoSidebar}
              onClose={() => setShowFriendInfoSidebar(false)}
            />
          )}
        </div>
      </div>
    );
  }

  // Default state - No friend or group selected
  return null;
};

export default ChatPage;

// WhatsApp-style tick indicators CSS
if (typeof document !== 'undefined') {
  const styleId = 'whatsapp-ticks-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .message-ticks {
        position: relative;
        width: 16px;
        height: 12px;
        display: inline-block;
        vertical-align: middle;
        user-select: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        pointer-events: none;
      }

      .tick {
        position: absolute;
        font-size: 12px;
        line-height: 12px;
        color: var(--color-text-muted);
        font-weight: 500;
        user-select: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        pointer-events: none;
      }

      /* Dark mode tick color */
      .dark .tick {
        color: var(--color-text-muted);
      }

      /* Tek tik */
      .message-ticks.single .tick-front {
        left: 0;
        top: 0;
      }

      /* Çift tik – arkadaki */
      .message-ticks.double .tick-back {
        left: 0;
        top: 2px;
      }

      /* Çift tik – öndeki */
      .message-ticks.double .tick-front {
        left: 4px;
        top: 0;
      }

      /* Okundu (mavi tik) */
      .message-ticks.read .tick {
        color: var(--color-primary);
      }

      /* Dark mode için mavi tik */
      .dark .message-ticks.read .tick {
        color: var(--color-primary);
      }

      /* Mesaj bubble - genel olarak seçilemez */
      .message-bubble {
        user-select: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
      }

      /* Mesaj içeriği - sadece text seçilebilir */
      .message-text {
        user-select: text !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        cursor: text;
      }

      /* Mesaj meta (saat, tik) - seçilemez */
      .message-meta,
      .message-meta *,
      .message-time,
      .message-ticks,
      .message-ticks *,
      .tick,
      .message-avatar,
      .message-avatar * {
        user-select: none !important;
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }
}

